import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { MEDIA } from "@/lib/content";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthShell title="Member Login" subtitle="Access your Moaglee command center.">
      <form onSubmit={submit} className="space-y-6" data-testid="login-form">
        {error && <p className="text-red-400 text-sm" data-testid="login-error">{error}</p>}
        <div>
          <label className="overline text-neutral-400 mb-2 block">Email</label>
          <input className="field-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
        </div>
        <div>
          <label className="overline text-neutral-400 mb-2 block">Password</label>
          <input className="field-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
        </div>
        <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="login-submit">
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Log In"}
        </button>
      </form>
      <div className="flex items-center gap-4 my-6">
        <div className="h-px bg-white/10 flex-1" /><span className="text-neutral-600 text-xs">OR</span><div className="h-px bg-white/10 flex-1" />
      </div>
      <button onClick={googleLogin} className="btn-ghost w-full justify-center" data-testid="google-login-btn">
        Continue with Google
      </button>
      <p className="text-neutral-500 text-sm mt-8 text-center">
        New here? <Link to="/register" className="text-[#CC5500] hover:underline" data-testid="to-register">Create an account</Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={MEDIA.building} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
        <Link to="/" className="absolute top-8 left-8 font-display text-2xl font-extrabold z-10">MOAGLEE<span className="text-[#CC5500]">.</span></Link>
        <div className="absolute bottom-10 left-8 right-8 z-10">
          <p className="font-display text-3xl font-bold">Real estate. Capital. Media. Ownership. Legacy.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16 bg-[#0A0A0A]">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden font-display text-2xl font-extrabold block mb-10">MOAGLEE<span className="text-[#CC5500]">.</span></Link>
          <h1 className="font-display text-3xl font-bold">{title}</h1>
          <p className="text-neutral-400 mt-2 mb-8">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
